// These MUI icons are temporary while the settings sidebar is being redesigned
import {
  Settings as AdminSettingsIcon,
  Receipt as BudgetApprovalsIcon,
  Flag as FeatureFlagsIcon,
  Translate,
} from '@mui/icons-material'
import { Box, Stack } from '@mui/material'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { useVisibleCatalogueSettings } from '@omnea-app/fe-settings/catalogues/useVisibleCatalogueSettings'
import { useSettingsLdFlags } from '@omnea-app/fe-settings/launch-darkly'
import { hasConfigPageAccess } from '@omnea-app/fe-settings/permissions/hasConfigPageAccess'
import { useOnlyAvailableInLocalAndQa } from '@omnea-app/frontend/utils/dev-exp/useOnlyAvailableInLocalAndQa'
import { FeatureFlagsEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { filterSectionsBySearch } from '../filterSectionsBySearch'
import { useIntegrationsItems } from '../hooks/useIntegrationsItems'
import { SidebarList, SidebarListItemButton, SidebarListItemText, SidebarSectionTitle } from '../StyledSidebarComponents'
import { getSettingsIconByName } from './SettingsIcon'
import { SettingsSearchbar } from './SettingsSearchbar'

export interface SettingsMenuItem {
  label: string
  icon: React.ReactElement
  href: string
  newTab?: boolean
  requiredFeatureFlags?: FeatureFlagsEnum[]
  /** If provided, the item is shown when ANY of these flags is enabled (OR logic). */
  requiredFeatureFlagsIncludes?: FeatureFlagsEnum[]
  requiredPermission?: {
    isAdmin?: boolean
    hasConfigPermissions?: boolean
    isOmnean?: boolean
    isLocalOrQa?: boolean
  }
  requiredLdFeatureFlags?: (keyof ReturnType<typeof useSettingsLdFlags>)[]
}

export interface SettingsSection {
  title: string
  items: SettingsMenuItem[]
  requiredPermission?: {
    isAdmin?: boolean
    hasConfigPermissions?: boolean
    isOmnean?: boolean
    isLocalOrQa?: boolean
  }
}

const filterMenuItem = (
  item: SettingsMenuItem,
  section: SettingsSection,
  featureFlags: ReturnType<typeof useFeatureFlags>,
  user: ReturnType<typeof useAuthenticatedUser>['user'],
  isLocalOrQa: boolean,
  ldFeatureFlags: ReturnType<typeof useSettingsLdFlags>
) => {
  const isFeatureFlagEnabled =
    (!item.requiredFeatureFlags || item.requiredFeatureFlags.every((flag) => featureFlags[flag])) &&
    (!item.requiredFeatureFlagsIncludes || item.requiredFeatureFlagsIncludes.some((flag) => featureFlags[flag]))
  if (!isFeatureFlagEnabled) {
    return false
  }

  // P2P_ADMIN_MODE (frontend-only HubSpot stop-gap): relaxes the config-permission requirement
  // for the workflows/forms items only. Route derived from the item href (e.g. /settings/workflows).
  const route = item.href.replace('/settings/', '')
  const hasConfigPermission = hasConfigPageAccess({ hasConfigPermissions: user.hasConfigPermissions, route, featureFlags })

  const hasSectionPermissions =
    !section.requiredPermission ||
    ((!section.requiredPermission.isAdmin || user.isAdmin) &&
      (!section.requiredPermission.hasConfigPermissions || hasConfigPermission) &&
      (!section.requiredPermission.isOmnean || user.isOmnean) &&
      (!section.requiredPermission.isLocalOrQa || isLocalOrQa))
  if (!hasSectionPermissions) {
    return false
  }

  const hasItemPermissions =
    !item.requiredPermission ||
    ((!item.requiredPermission.isAdmin || user.isAdmin) &&
      (!item.requiredPermission.hasConfigPermissions || hasConfigPermission) &&
      (!item.requiredPermission.isOmnean || user.isOmnean) &&
      (!item.requiredPermission.isLocalOrQa || isLocalOrQa))
  if (!hasItemPermissions) {
    return false
  }

  const hasLdFeatureFlagsEnabled =
    !item.requiredLdFeatureFlags || item.requiredLdFeatureFlags.every((flag) => ldFeatureFlags[flag])
  if (!hasLdFeatureFlagsEnabled) {
    return false
  }

  return true
}

const PREVENT_BROWSER_URL_TOOLTIP_OVERLAP_PADDING = 14

export const SettingsSidebarContent = () => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState('')
  const { user } = useAuthenticatedUser()
  const isLocalOrQa = useOnlyAvailableInLocalAndQa()
  const featureFlags = useFeatureFlags()
  const ldFeatureFlags = useSettingsLdFlags()
  const integrationsItems = useIntegrationsItems(user.isAdmin && user.hasConfigPermissions)
  const hasVisibleCatalogueSettings = useVisibleCatalogueSettings().length > 0

  const personalSettingSection: SettingsSection = {
    title: t('sidebar.account-title'),
    items: [
      { label: t('sidebar.notifications'), icon: getSettingsIconByName('notifications'), href: '/settings/notifications' },
      {
        label: t('sidebar.preferences'),
        icon: getSettingsIconByName('preferences'),
        href: '/settings/preferences',
      },
    ],
  }

  const organisationSection: SettingsSection = {
    title: t('sidebar.organisation-title'),
    requiredPermission: { isAdmin: true, hasConfigPermissions: true },
    items: [
      {
        label: t('sidebar.default-notifications'),
        icon: getSettingsIconByName('notifications'),
        href: '/settings/default-notifications',
      },
      {
        label: t('sidebar.ai'),
        icon: getSettingsIconByName('ai'),
        href: '/settings/ai',
      },
      {
        label: t('sidebar.security'),
        icon: getSettingsIconByName('security'),
        href: '/settings/security',
      },
      {
        label: t('sidebar.core-data'),
        icon: getSettingsIconByName('core-data'),
        href: '/settings/core-data',
      },
      {
        label: t('sidebar.users-and-groups'),
        icon: getSettingsIconByName('users'),
        href: '/settings/users-and-groups',
      },
      {
        label: t('sidebar.user-properties'),
        icon: getSettingsIconByName('user-properties'),
        href: '/settings/user-properties',
      },
      {
        label: t('sidebar.api'),
        icon: getSettingsIconByName('api'),
        href: '/settings/api',
        requiredFeatureFlags: [FeatureFlagsEnum.PUBLIC_API_UI, FeatureFlagsEnum.PUBLIC_API],
      },
      {
        label: t('sidebar.import'),
        icon: getSettingsIconByName('import'),
        href: '/settings/import',
      },
      {
        label: t('sidebar.audit-log'),
        icon: getSettingsIconByName('audit-log'),
        href: '/settings/audit-log',
      },
      {
        label: t('sidebar.external-archive'),
        icon: getSettingsIconByName('audit-log'),
        href: '/settings/external-archive',
        requiredPermission: { isOmnean: true },
      },
    ],
  }

  const featuresSection: SettingsSection = {
    title: t('sidebar.features-title'),
    requiredPermission: { isAdmin: true, hasConfigPermissions: true },
    items: [
      {
        label: t('sidebar.workflows'),
        icon: getSettingsIconByName('workflows'),
        href: '/settings/workflows',
      },
      {
        label: t('sidebar.budget-approvals'),
        icon: <BudgetApprovalsIcon sx={{ width: 18 }} />,
        href: '/settings/dynamic-assignees',
      },
      {
        label: t('sidebar.forms'),
        icon: getSettingsIconByName('questions'),
        href: '/settings/forms',
      },
      {
        label: t('sidebar.sourcing'),
        icon: getSettingsIconByName('sourcing'),
        href: '/settings/sourcing',
        requiredFeatureFlags: [FeatureFlagsEnum.RFX],
      },
      {
        label: t('sidebar.auctions'),
        icon: getSettingsIconByName('auctions'),
        href: '/settings/auctions',
        requiredFeatureFlags: [FeatureFlagsEnum.AUCTIONS],
      },
      {
        label: t('sidebar.risk-management'),
        icon: getSettingsIconByName('risk-management'),
        href: '/settings/risks',
        requiredFeatureFlags: [FeatureFlagsEnum.TPRM],
      },
      ...(hasVisibleCatalogueSettings
        ? [
            {
              label: t('sidebar.catalogues'),
              icon: getSettingsIconByName('catalogues'),
              href: '/settings/catalogues',
            },
          ]
        : []),
      {
        label: t('sidebar.product-supplier-categories'),
        icon: getSettingsIconByName('product-supplier-categories'),
        href: '/settings/categories',
        requiredFeatureFlagsIncludes: [
          FeatureFlagsEnum.PRODUCTS_AND_SERVICES_CATALOGUE,
          FeatureFlagsEnum.SUPPLIER_CATEGORISATION,
        ],
      },
      {
        label: ldFeatureFlags.SHOW_DOCUMENT_REVIEW ? t('sidebar.document-review-playbooks') : t('sidebar.document-storage'),
        icon: getSettingsIconByName('document-storage'),
        href: '/settings/document-storage',
        requiredFeatureFlags: [FeatureFlagsEnum.ENABLE_DOCUMENT_EXTRACTION],
      },
      {
        label: t('sidebar.policies'),
        icon: getSettingsIconByName('policies'),
        href: '/settings/policies',
        requiredLdFeatureFlags: ['RELEASE_POLICY_STORE'],
      },
      {
        label: t('sidebar.feature-flags', 'Feature flags'),
        icon: <FeatureFlagsIcon sx={{ width: 18, height: 18 }} />,
        href: '/settings/__feature-flags',
        requiredPermission: { isOmnean: true },
      },
      {
        label: t('sidebar.localisation'),
        icon: <Translate sx={{ width: 18, height: 18 }} />,
        href: '/settings/localisation',
        requiredFeatureFlags: [FeatureFlagsEnum.CONFIG_TRANSLATIONS],
      },
      {
        label: t('sidebar.admin-settings'),
        icon: <AdminSettingsIcon sx={{ width: 18, height: 18 }} />,
        href: '/settings/admin',
      },
      {
        label: t('sidebar.agreements-migration'),
        icon: <AdminSettingsIcon sx={{ width: 18, height: 18 }} />,
        href: '/settings/agreements-migration',
        requiredPermission: { isOmnean: true },
        requiredFeatureFlags: [FeatureFlagsEnum.CREATE_AGREEMENT],
      },
    ],
  }

  const accountsPayableSection: SettingsSection = {
    title: t('sidebar.accounts-payable-title'),
    requiredPermission: { isAdmin: true, hasConfigPermissions: true },
    items: [
      {
        label: t('sidebar.goods-receipting'),
        icon: getSettingsIconByName('goods-receipting'),
        href: '/settings/goods-receipting',
        requiredFeatureFlags: [FeatureFlagsEnum.GOODS_RECEIPTING],
      },
    ],
  }

  const integrationsSection: SettingsSection = {
    title: t('sidebar.integrations'),
    requiredPermission: { isAdmin: true, hasConfigPermissions: true },
    items: integrationsItems,
  }

  const sections: SettingsSection[] = [
    personalSettingSection,
    organisationSection,
    featuresSection,
    accountsPayableSection,
    integrationsSection,
  ]

  const permissionFilteredSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => filterMenuItem(item, section, featureFlags, user, isLocalOrQa, ldFeatureFlags)),
    }))
    .filter((section) => section.items.length > 0)

  const filteredSections = filterSectionsBySearch(permissionFilteredSections, searchQuery)

  return (
    <Stack spacing={3} sx={{ height: '100%', overflow: 'hidden' }}>
      {/* Menu sections */}
      <Stack
        spacing={2}
        sx={{
          pb: PREVENT_BROWSER_URL_TOOLTIP_OVERLAP_PADDING,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <SettingsSearchbar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        {filteredSections.map((section: SettingsSection) => (
          <Box key={section.title}>
            <SidebarSectionTitle>{section.title}</SidebarSectionTitle>
            <SidebarList>
              {section.items.map((item: SettingsMenuItem) => {
                const isSelected = router.asPath === item.href
                const newTabProps = item.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}
                return (
                  <Link key={item.href} href={item.href} passHref {...newTabProps}>
                    <SidebarListItemButton selected={isSelected}>
                      <Box sx={{ minWidth: 24, color: 'sidebar.icon', mr: 2, mt: 1 }}>{item.icon}</Box>
                      <SidebarListItemText
                        primary={item.label}
                        secondary={item.requiredPermission?.isOmnean ? t('sidebar.omnean-only') : undefined}
                      />
                    </SidebarListItemButton>
                  </Link>
                )
              })}
            </SidebarList>
          </Box>
        ))}
      </Stack>
    </Stack>
  )
}
